/**
 * Reflection Prompt Library
 *
 * Curated prompts for guided journaling, grounded in ADHD psychology research.
 * Static data — versioned with code, not stored in Automerge.
 *
 * Categories map to PromptCategory from ./meta.ts.
 * Selection logic lives in useReflectionPrompt hook.
 */

import type { PromptCategory } from './meta'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReflectionPrompt {
  id: string
  text: string
  category: PromptCategory
  frequency: 'daily' | 'weekly' | 'monthly' | 'occasional'
  depth: 'light' | 'medium' | 'deep'
  timeOfDay: 'morning' | 'evening' | 'any'
  requiresPeople?: boolean
  requiresCompletedTasks?: boolean
}

// ---------------------------------------------------------------------------
// Prompt Library
// ---------------------------------------------------------------------------

export const REFLECTION_PROMPTS: ReflectionPrompt[] = [
  // ── Daily Check-ins (light, daily) ──────────────────────────────────
  {
    id: 'daily-today-i-am',
    text: 'Today I am...',
    category: 'daily-checkin',
    frequency: 'daily',
    depth: 'light',
    timeOfDay: 'any',
  },
  {
    id: 'daily-focus',
    text: "What's the one thing I want to focus on today?",
    category: 'daily-checkin',
    frequency: 'daily',
    depth: 'light',
    timeOfDay: 'morning',
  },
  {
    id: 'daily-looking-forward',
    text: 'What am I looking forward to?',
    category: 'daily-checkin',
    frequency: 'daily',
    depth: 'light',
    timeOfDay: 'morning',
  },
  {
    id: 'daily-went-well',
    text: 'What went well today?',
    category: 'daily-checkin',
    frequency: 'daily',
    depth: 'light',
    timeOfDay: 'evening',
  },

  // ── Joy & Gratitude (light, 2-3x/week) ─────────────────────────────
  {
    id: 'joy-today',
    text: 'What happened today that brought me joy?',
    category: 'joy-gratitude',
    frequency: 'weekly',
    depth: 'light',
    timeOfDay: 'evening',
  },
  {
    id: 'joy-grateful',
    text: "What's one thing I'm grateful for right now?",
    category: 'joy-gratitude',
    frequency: 'weekly',
    depth: 'light',
    timeOfDay: 'any',
  },
  {
    id: 'joy-smile',
    text: 'What made me smile today?',
    category: 'joy-gratitude',
    frequency: 'weekly',
    depth: 'light',
    timeOfDay: 'evening',
  },
  {
    id: 'joy-grateful-person',
    text: 'Who am I grateful for, and why?',
    category: 'joy-gratitude',
    frequency: 'weekly',
    depth: 'light',
    timeOfDay: 'any',
    requiresPeople: true,
  },

  // ── Challenge & Anxiety (medium, 2x/week) ──────────────────────────
  {
    id: 'challenge-anxious',
    text: 'What happened today that made me anxious?',
    category: 'challenge-anxiety',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'evening',
  },
  {
    id: 'challenge-frustrated',
    text: 'What tasks frustrated me today?',
    category: 'challenge-anxiety',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'evening',
  },
  {
    id: 'challenge-overwhelming',
    text: 'What felt overwhelming, and why?',
    category: 'challenge-anxiety',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'challenge-reaction',
    text: 'What situation triggered a strong reaction? What was underneath it?',
    category: 'challenge-anxiety',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },

  // ── Accomplishment / Anti-Success-Amnesia (medium, weekly) ─────────
  {
    id: 'accomplish-proud',
    text: 'What did I finish today that I should be proud of?',
    category: 'accomplishment',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'evening',
    requiresCompletedTasks: true,
  },
  {
    id: 'accomplish-hard',
    text: 'What did I do this week that was hard, but I did it anyway?',
    category: 'accomplishment',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'accomplish-three',
    text: 'Write down three accomplishments from this past week.',
    category: 'accomplishment',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'accomplish-month',
    text: "What's my biggest accomplishment from last month?",
    category: 'accomplishment',
    frequency: 'monthly',
    depth: 'medium',
    timeOfDay: 'any',
  },

  // ── People & Connection (medium, weekly, people-linked) ────────────
  {
    id: 'people-reconnect',
    text: 'Think about someone you care about. When did you last connect? How are they?',
    category: 'people-connection',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
    requiresPeople: true,
  },
  {
    id: 'people-appreciate',
    text: "What's one thing I appreciate about someone close to me?",
    category: 'people-connection',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
    requiresPeople: true,
  },
  {
    id: 'people-reach-out',
    text: 'Who would I like to reach out to this week?',
    category: 'people-connection',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'morning',
    requiresPeople: true,
  },
  {
    id: 'people-energy',
    text: 'Who energized me recently? Who drained me?',
    category: 'people-connection',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
    requiresPeople: true,
  },

  // ── Self-Awareness & Strengths (medium-deep, weekly) ───────────────
  {
    id: 'self-strengths',
    text: 'What are my greatest strengths? What are their pros and cons?',
    category: 'self-awareness',
    frequency: 'weekly',
    depth: 'deep',
    timeOfDay: 'any',
  },
  {
    id: 'self-burnout',
    text: 'What are my small signs of burnout? Am I noticing any right now?',
    category: 'self-awareness',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'self-norms',
    text: "Where do 'normal' expectations not work for me? What works instead?",
    category: 'self-awareness',
    frequency: 'weekly',
    depth: 'deep',
    timeOfDay: 'any',
  },
  {
    id: 'self-emotion-body',
    text: 'What emotion am I feeling right now? Where do I notice it in my body?',
    category: 'self-awareness',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },

  // ── Hyperfocus & Energy (medium, weekly) ───────────────────────────
  {
    id: 'hyper-flow',
    text: 'What sent me into hyperfocus recently? How did it feel?',
    category: 'hyperfocus-energy',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'hyper-energy-level',
    text: 'How is my energy today? What helped or drained it?',
    category: 'hyperfocus-energy',
    frequency: 'daily',
    depth: 'light',
    timeOfDay: 'evening',
  },
  {
    id: 'hyper-burnout-cycle',
    text: 'Am I in a push-crash cycle right now? What would "enough" look like today?',
    category: 'hyperfocus-energy',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'hyper-dopamine',
    text: 'What gave me a dopamine hit today? Was it helpful or a distraction?',
    category: 'hyperfocus-energy',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'evening',
  },

  // ── Emotional Reaction (medium-deep, weekly) ──────────────────────
  {
    id: 'emotion-intense',
    text: 'When did I feel the most intense emotion today? What triggered it?',
    category: 'emotional-reaction',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'evening',
  },
  {
    id: 'emotion-knee-jerk',
    text: 'Did I have a knee-jerk reaction today I wish I could replay? What would I do differently?',
    category: 'emotional-reaction',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'evening',
  },
  {
    id: 'emotion-rsd',
    text: 'Did I feel rejected or criticized today? What actually happened vs. what I felt?',
    category: 'emotional-reaction',
    frequency: 'weekly',
    depth: 'deep',
    timeOfDay: 'evening',
  },
  {
    id: 'emotion-masking',
    text: 'When did I feel like I was performing today instead of being myself?',
    category: 'emotional-reaction',
    frequency: 'weekly',
    depth: 'deep',
    timeOfDay: 'evening',
  },

  // ── Weekly Review (structured, weekly) ─────────────────────────────
  {
    id: 'weekly-change',
    text: 'One thing I want to change.',
    category: 'weekly-review',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'weekly-more',
    text: 'One thing I want to do more of.',
    category: 'weekly-review',
    frequency: 'weekly',
    depth: 'medium',
    timeOfDay: 'any',
  },
  {
    id: 'weekly-grateful',
    text: 'One thing I am grateful for.',
    category: 'weekly-review',
    frequency: 'weekly',
    depth: 'light',
    timeOfDay: 'any',
  },
  {
    id: 'weekly-proud',
    text: 'One thing to be proud of myself for.',
    category: 'weekly-review',
    frequency: 'weekly',
    depth: 'light',
    timeOfDay: 'any',
  },

  // ── Deep / Monthly (deep, monthly/occasional) ─────────────────────
  {
    id: 'deep-letter',
    text: "Write a letter to yourself when you're struggling. Remind yourself what you're capable of.",
    category: 'self-awareness',
    frequency: 'monthly',
    depth: 'deep',
    timeOfDay: 'any',
  },
  {
    id: 'deep-conform',
    text: "What's one norm you've tried to conform to that doesn't actually work for you?",
    category: 'self-awareness',
    frequency: 'monthly',
    depth: 'deep',
    timeOfDay: 'any',
  },
  {
    id: 'deep-mask',
    text: 'If you could drop one mask, which would it be?',
    category: 'self-awareness',
    frequency: 'occasional',
    depth: 'deep',
    timeOfDay: 'any',
  },
  {
    id: 'deep-others',
    text: 'List five things others have told you about yourself. Which are true, and which are their projection?',
    category: 'self-awareness',
    frequency: 'occasional',
    depth: 'deep',
    timeOfDay: 'any',
  },
]

// ---------------------------------------------------------------------------
// Restart Prompts (triggered by 3+ day gap)
// ---------------------------------------------------------------------------

export const RESTART_PROMPTS: ReflectionPrompt[] = [
  {
    id: 'restart-back',
    text: "I'm back. Today I am...",
    category: 'daily-checkin',
    frequency: 'occasional',
    depth: 'light',
    timeOfDay: 'any',
  },
  {
    id: 'restart-happened',
    text: "It's been a while. What's one thing that happened since I last wrote?",
    category: 'daily-checkin',
    frequency: 'occasional',
    depth: 'light',
    timeOfDay: 'any',
  },
]

// ---------------------------------------------------------------------------
// Lookup Helpers
// ---------------------------------------------------------------------------

/**
 * Get all prompts in a given category.
 */
export function getPromptsByCategory(
  category: PromptCategory,
): ReflectionPrompt[] {
  return REFLECTION_PROMPTS.filter((p) => p.category === category)
}

/**
 * Find a prompt by ID (searches both main library and restart prompts).
 */
export function getPromptById(id: string): ReflectionPrompt | undefined {
  return (
    REFLECTION_PROMPTS.find((p) => p.id === id) ??
    RESTART_PROMPTS.find((p) => p.id === id)
  )
}
